// @mui
import { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Grid, Container, Typography, Button, Card, Stack, Chip, LinearProgress } from '@mui/material';
// components
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Page from '../components/Page';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import * as actions from '../store/actions';
import constants from '../constants';
import Iconify from '../components/Iconify';
import StorageManager from '../services/StorageManager';
import * as StorageKeys from '../services/StorageKeys';
import HowToUsePopup from '../ui-component/HowToUsePopup';
import { getUsageAccessInfo } from '../services/helper';
// ----------------------------------------------------------------------

export default function DashboardApp() {
  const { hl, t } = useLanguage();
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showTutorialPoup, setShowTutorialPoup] = useState(false);
  const homeTitle = t('home.title'); // Dashboard
  const labelStartTyping = t('labelStartTyping'); // Start Typing
  const labelTrialLeft = t('labelTrialLeft'); // trials left
  const homeBottomNote = t('home.bottomNote'); // Now you can automatically translate your spoken language into another language
  const setHere = t('home.setH'); // Set here
  const labelGoPremium = t('labelGoPremium'); // Go Premium
  const trialEnd = t('trialEnd'); // Trial Ended
  const doTranslate = useSelector((state) => state.pLanguage.translate);
  const targetLang = useSelector((state) => state.pLanguage.targetLanguage);
  const voiceStarted = useSelector((state) => state.voice.start);
  const startedListeningFor = t('listenFor'); // Listening for
  const and = t('and'); // And
  const typingInto = t('typingInto'); // Typing into
  const startedLabel = doTranslate
    ? `${startedListeningFor} <b>${hl.name}</b> ${and} ${typingInto} <b>${targetLang}</b>...`
    : `${startedListeningFor} <b>${hl.name}</b>...`;

  const accessInfo = getUsageAccessInfo(currentUser);
  const premiumLabel = accessInfo.isRestricted ? `${trialEnd}.` : `${accessInfo.trialsLeft} ${labelTrialLeft}`;


  useEffect(() => {
    const data = StorageManager.get(StorageKeys.SHOW_HOW_TO_USE_POPUP);
    if (data !== 'DONE') {
      setShowTutorialPoup(true);
      StorageManager.put(StorageKeys.SHOW_HOW_TO_USE_POPUP, 'DONE');
    }
  }, []);

  const onClickMic = () => {
    navigate('/dashboard/app/started');
  };
  const onClickGoPremium = () => {
    navigate('/dashboard/payment');
  };
  const onLanguageClick = () => {
    dispatch({ type: actions.LANGUAGE_CHANGE_START });
  };

  const onTranslateLanguageClick = () => {
    navigate('/dashboard/settings');
  };

  const usageProgress = accessInfo.paid ? 100 : Math.min(accessInfo.usedCount / constants.freeLimit * 100, 100);
  const waveformBars = [18, 34, 26, 48, 68, 42, 58, 78, 52, 32, 64, 44, 74, 38, 56, 30];
  const voiceWorkflows = [
    { title: 'Speak', value: hl?.name, action: onLanguageClick },
    { title: 'Output', value: doTranslate && targetLang ? targetLang : 'Same language', action: onTranslateLanguageClick },
    { title: 'Cleanup', value: 'Polished', action: () => navigate('/dashboard/commands') },
  ];

  return (
    <>
      {showTutorialPoup && <HowToUsePopup isOpen={showTutorialPoup} />}
      <Page title={homeTitle}>
        <Container maxWidth="xl">
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} lg={8}>
              <Card
                sx={{
                  height: '100%',
                  p: { xs: 3, md: 5 },
                  color: 'common.white',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, #171717 0%, #23221F 58%, #0F3D28 100%)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 'auto -10% -45% auto',
                    width: 340,
                    height: 340,
                    borderRadius: '50%',
                    bgcolor: alpha('#FFFFFF', 0.12),
                  }}
                />
                <Stack spacing={3} sx={{ position: 'relative', maxWidth: 720 }}>
                  <Chip
                    label={voiceStarted ? 'REC Voice Typing' : 'Ready to Flow'}
                    sx={{
                      alignSelf: 'flex-start',
                      color: 'common.white',
                      bgcolor: voiceStarted ? 'error.main' : alpha('#FFFFFF', 0.14),
                      fontWeight: 700,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{ mb: 1.5, color: 'common.white', lineHeight: 1.08 }}
                    >
                      Do not type.
                      <br />
                      Just speak.
                    </Typography>
                    <Typography sx={{ color: alpha('#FFFFFF', 0.78), maxWidth: 620 }}>
                      Turn natural speech into clear, polished writing across Gmail, Docs, support tools, chats, and any browser text field.
                    </Typography>
                  </Box>
                  <Card sx={{ p: 2, bgcolor: alpha('#FFFFFF', 0.08), border: `1px solid ${alpha('#FFFFFF', 0.12)}`, boxShadow: 0 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ color: 'common.white', fontWeight: 700 }}>
                          {voiceStarted ? startedLabel.replace(/<[^>]+>/g, '') : 'Press Start Typing and speak naturally'}
                        </Typography>
                        <Chip size="small" label={hl?.name} sx={{ bgcolor: alpha('#FFFFFF', 0.14), color: 'common.white' }} />
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: 70 }}>
                        {waveformBars.map((height, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: 8,
                              height,
                              borderRadius: 99,
                              bgcolor: voiceStarted ? 'secondary.main' : alpha('#FFFFFF', 0.34),
                              opacity: voiceStarted ? 1 : 0.68,
                            }}
                          />
                        ))}
                      </Stack>
                      <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.64) }}>
                        Voice input · {doTranslate && targetLang ? `${hl?.name} to ${targetLang}` : `${hl?.name} auto punctuation`}
                      </Typography>
                    </Stack>
                  </Card>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      size="large"
                      variant="contained"
                      color={voiceStarted ? 'error' : 'primary'}
                      onClick={onClickMic}
                      startIcon={<Iconify icon="emojione-monotone:studio-microphone" />}
                      sx={{
                        bgcolor: 'common.white',
                        color: voiceStarted ? 'error.main' : 'primary.darker',
                        '&:hover': { bgcolor: alpha('#FFFFFF', 0.9) },
                      }}
                    >
                      {voiceStarted ? startedListeningFor : labelStartTyping}
                    </Button>
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={onLanguageClick}
                      startIcon={<Iconify icon="fa-solid:language" />}
                      sx={{
                        color: 'common.white',
                        borderColor: alpha('#FFFFFF', 0.38),
                        '&:hover': { borderColor: 'common.white', bgcolor: alpha('#FFFFFF', 0.08) },
                      }}
                    >
                      Language: {hl?.name}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                      Account status
                    </Typography>
                    <Typography variant="h5">{accessInfo.paid ? 'Premium active' : premiumLabel}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={usageProgress}
                    color={accessInfo.isRestricted ? 'error' : 'primary'}
                    sx={{ height: 10, borderRadius: 5, bgcolor: 'background.neutral' }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {accessInfo.paid
                      ? 'Unlimited voice typing is enabled for this account.'
                      : `${accessInfo.trialsLeft} ${labelTrialLeft}. ${accessInfo.daysLeft} free days left.`}
                  </Typography>
                  {!accessInfo.paid && (
                    <Button variant="contained" onClick={onClickGoPremium} startIcon={<Iconify icon="ic:sharp-workspace-premium" />}>
                      {labelGoPremium}
                    </Button>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            {voiceWorkflows.map((workflow) => (
              <Grid item xs={12} md={4} key={workflow.title}>
                <Card
                  onClick={workflow.action}
                  sx={{
                    p: 2,
                    height: '100%',
                    cursor: 'pointer',
                    boxShadow: 0,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:hover': { borderColor: 'secondary.main' },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                    <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                      {workflow.title}
                    </Typography>
                    <Typography variant="subtitle1">{workflow.value}</Typography>
                    </Box>
                    <Iconify icon="eva:chevron-right-fill" width={20} height={20} />
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={12}>
              <Alert
                sx={{ display: 'flex', alignItems: 'center', mt: 3, borderRadius: 2, bgcolor: 'common.white' }}
                variant="outlined"
                severity="info"
                icon={<Iconify icon="fluent-emoji-flat:new-button" />}
              >
                {homeBottomNote}{' '}
                <Button
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={onTranslateLanguageClick}
                  style={{ textDecoration: 'none' }}
                >
                  <b>{setHere}</b>
                </Button>
                .
              </Alert>
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
